import axios, { AxiosResponse } from 'axios';
import { Color } from '../enums/Color.enum';
import { DataLayerEvent, DataLayerProduct, DataLayerVariant, ProductPayload } from '../models/DataLayer.model';
import { HoneyloveWindow } from '../models/HoneyloveWindow';
import { ShopifyCheckout } from '../models/ShopifyCheckout.model';
import { ShopifyLineItem } from '../models/ShopifyLineItem.model';
import { LongSize } from '../enums/Size.enum';
import {
  userDetected,
  checkoutStepEncountered,
  purchase,
  paymentGatewayUsed,
  postPurchaseUserResponseEvent,
  pageInitialized,
  experimentViewed,
} from './dataLayerEvents.client';
import { environmentContext } from '../environment-context';

declare const window: HoneyloveWindow;
type PushResponse = Promise<void | AxiosResponse<StreamResponse>>;

export const trackPostPurchaseUserResponse = (response: Pick<DataLayerEvent, 'postPurchaseUserResponse'>): void => {
  pushEvent(postPurchaseUserResponseEvent(response));
};

export const trackUserDetected = (externalId: string, userEmail: string): PushResponse => pushEvent(userDetected(externalId, userEmail));

export const trackPageInitialized = (): PushResponse => pushEvent(pageInitialized());

export const trackCheckoutStepEncountered = (productList: ProductPayload[], step: string, option: string): PushResponse =>
  pushEvent(checkoutStepEncountered(productList, step, option));

export const trackPurchase = (shopifyCheckout: ShopifyCheckout, productSets: ProductPayload[]): void => {
  pushEvent(purchase(shopifyCheckout, productSets));
};

export const trackExperimentViewed = (experimentId: string, variationId: number): PushResponse => pushEvent(experimentViewed(experimentId, variationId));

export const trackPaymentGatewayUsed = (gateway: string): PushResponse => pushEvent(paymentGatewayUsed(gateway));

export const lineItemToProduct = (item: ShopifyLineItem, list?: string): DataLayerProduct => ({
  id: item.product_id,
  handle: item.handle,
  title: item.product_title,
  sku: item.sku?.split('-')[0],
  price: item.price > 1000 ? item.price / 100 : item.price, // TODO remove the divide by 100 post SSR (if it's not relevant anymore)
  vendor: item.vendor,
  type: item.product_type,
  list,
});

export const lineItemToVariant = (item: ShopifyLineItem): DataLayerVariant => ({
  title: item.variant_title,
  color: item.variant_options[0] as Color,
  quantity: item.quantity,
  size: item.variant_options[1] as LongSize,
  sku: item.sku,
  id: item.variant_id,
  featured_image: { src: item.image },
});

// Math.random should be unique because of its seeding algorithm.
// Convert it to base 36 (numbers + letters), and grab the first 9 characters
// after the decimal.
const eventID = () => Date.now() + '_' + Math.random().toString(36).substr(2, 9);

interface StreamResponse {
  id: string;
}

export const externalIdKey = 'hlExternalId';

const pushEvent = (payload: DataLayerEvent): PushResponse => {
  if (typeof window !== 'undefined') {
    const externalId = sessionStorage.getItem(externalIdKey) ?? undefined;
    // note: we hash email on the server
    const augmentedEvent = {
      ...payload,
      eventID: eventID(),
      externalId,
      email: payload.email,
      gaClientId: window.ga?.getAll?.()?.[0]?.get('clientId'),
      gaSessionId: window.ga?.getAll?.()?.[1]?.get('dimension6'),
      eventSourceUrl: window?.document.location.href,
    };
    window.dataLayer = window.dataLayer || [];
    // add an event ID to all items for FB serverside linkage
    window.dataLayer.push(augmentedEvent);

    const streamEvents = [
      'NavigationLinkClicked', // CustomEvent
      'UserDetected', // CustomEvent
      'ProductDetailViewed', // ViewContent
      'ProductColorChanged', // CustomizeProduct
      'ProductSizeChanged', // CustomizeProduct
      'SizingToolProductSelected', // CustomizeProduct
      'CartItemAdded', // AddToCart
      'CheckoutStepEncountered', // step: 1 = InitiateCheckout
      'Purchase', // Purchase && AddPaymentInfo
      'SizingToolResponse', // Sizing Tool Responses
      'QuizToolResponse', // Quiz Tool Responses
      'PostPurchaseResponse', // Post Purchase Survey Responses
      'PageInitialized', // Page Initialized, used for initial receipt of externalId
    ];

    const url = `https://${environmentContext.dataApiDomain}/api/stream`;
    if (streamEvents.includes(payload.event)) {
      return axios.post<StreamResponse>(url, augmentedEvent, { withCredentials: true }).catch((error) => {
        window.Sentry.captureException(error);
      });
    }
  }
  return new Promise(() => undefined);
};
